import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  TextField,
  Typography,
  Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getAdvertisementAnalyticsApi,
  exportAdvertisementAnalyticsApi,
  getAdvertisementMetaApi,
} from "../../../redux/api/api";
import {
  KpiRow,
  TrendChart,
  defaultRange,
  RANGE_PRESETS,
  rangeForPreset,
  formatNumber,
  formatPercent,
  formatInr,
} from "./AdPerformance";

const BreakdownTable = ({ title, rows, labelKey, labelFor }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {title}
      </Typography>
      {rows.length ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell align="right">Impr.</TableCell>
                <TableCell align="right">Clicks</TableCell>
                <TableCell align="right">CTR</TableCell>
                <TableCell align="right">Conv.</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r[labelKey] || "none"}>
                  <TableCell>{labelFor(r[labelKey])}</TableCell>
                  <TableCell align="right">{formatNumber(r.impressions)}</TableCell>
                  <TableCell align="right">{formatNumber(r.clicks)}</TableCell>
                  <TableCell align="right">{formatPercent(r.ctr)}</TableCell>
                  <TableCell align="right">{formatNumber(r.conversions)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No data for this range.
        </Typography>
      )}
    </CardContent>
  </Card>
);

/**
 * Marketing Dashboard — campaign performance across every ad, from
 * GET /api/admin/advertisement/analytics/overview. Filters: date range
 * (IST days), service, placement, source; CSV export of the same filters.
 */
const MarketingDashboard = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    ...defaultRange(),
    serviceId: "",
    placement: "",
    sourceType: "",
  });
  const [meta, setMeta] = useState({ services: [], placementCatalog: [] });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const params = Object.fromEntries(
    Object.entries(filters).filter(([, v]) => v),
  );

  useEffect(() => {
    getAdvertisementMetaApi()
      .then((res) => setMeta(res?.data?.info || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    getAdvertisementAnalyticsApi(params)
      .then((res) => setData(res?.data?.info))
      .catch((error) =>
        toast.error(
          error?.response?.data?.message || "Unable to load analytics.",
        ),
      )
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const setFilter = (patch) => setFilters((prev) => ({ ...prev, ...patch }));
  const placementLabel = (id) =>
    (meta.placementCatalog || []).find((p) => p.id === id)?.label ||
    (id || "—").replace(/_/g, " ");

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportAdvertisementAnalyticsApi(params);
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `ad-analytics-${filters.from}-to-${filters.to}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Unable to export analytics.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="breadcrumb-card">
        <h5>Marketing Dashboard</h5>
        <ul className="breadcrumb">
          <li>
            <Link to="/dashboard/ecommerce/">
              <i className="material-symbols-outlined">home</i>
              Dashboard
            </Link>
          </li>
          <li>Marketing</li>
          <li>Dashboard</li>
        </ul>
      </div>

      <Card sx={{ mb: 2 }}>
        <CardContent
          sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}
        >
          <ButtonGroup size="small">
            {RANGE_PRESETS.map((p) => (
              <Button key={p.days} onClick={() => setFilter(rangeForPreset(p.days))}>
                {p.label}
              </Button>
            ))}
          </ButtonGroup>
          <TextField
            size="small"
            type="date"
            label="From"
            InputLabelProps={{ shrink: true }}
            value={filters.from}
            onChange={(e) => setFilter({ from: e.target.value })}
          />
          <TextField
            size="small"
            type="date"
            label="To"
            InputLabelProps={{ shrink: true }}
            value={filters.to}
            onChange={(e) => setFilter({ to: e.target.value })}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={filters.serviceId}
              onChange={(e) => setFilter({ serviceId: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {(meta.services || []).map((s) => (
                <MenuItem key={s} value={s}>
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Placement</InputLabel>
            <Select
              label="Placement"
              value={filters.placement}
              onChange={(e) => setFilter({ placement: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              {(meta.placementCatalog || []).map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Source</InputLabel>
            <Select
              label="Source"
              value={filters.sourceType}
              onChange={(e) => setFilter({ sourceType: e.target.value })}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="IN_APP">In-app</MenuItem>
              <MenuItem value="THIRD_PARTY">Third-party</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            disabled={exporting}
            onClick={handleExport}
          >
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        </CardContent>
      </Card>

      {loading && !data ? (
        <Typography sx={{ p: 2 }}>Loading analytics…</Typography>
      ) : data ? (
        <>
          <KpiRow totals={data.totals} reachPartial={data.reachPartial} />
          {!data.totals.impressions ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No impressions in this range yet. Tracking starts once customers
              are on an app build that reports ad events.
            </Alert>
          ) : null}

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2">Daily trend</Typography>
              <TrendChart daily={data.daily} />
            </CardContent>
          </Card>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
              gap: 2,
              mb: 2,
            }}
          >
            <BreakdownTable
              title="By placement"
              rows={data.byPlacement}
              labelKey="placement"
              labelFor={placementLabel}
            />
            <BreakdownTable
              title="By category"
              rows={data.byService}
              labelKey="serviceId"
              labelFor={(s) =>
                s === "other"
                  ? "No category (third-party)"
                  : s
                    ? s.charAt(0).toUpperCase() + s.slice(1)
                    : "—"
              }
            />
            <BreakdownTable
              title="By source"
              rows={data.bySource}
              labelKey="sourceType"
              labelFor={(s) => (s === "THIRD_PARTY" ? "Third-party" : "In-app")}
            />
          </Box>

          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Campaign performance
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Campaign</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Placement</TableCell>
                      <TableCell align="right">Impressions</TableCell>
                      <TableCell align="right">Clicks</TableCell>
                      <TableCell align="right">CTR</TableCell>
                      <TableCell align="right">Conversions</TableCell>
                      <TableCell align="right">Spend</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.campaigns.map((c) => (
                      <TableRow
                        key={c.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => navigate(`/marketing/advertisements/${c.id}`)}
                      >
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {c.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {c.campaignId} · {c.serviceId}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {c.sourceType === "THIRD_PARTY" ? (
                            <Chip size="small" label={c.advertiserName || "Third-party"} />
                          ) : (
                            "In-app"
                          )}
                        </TableCell>
                        <TableCell>{placementLabel(c.placement)}</TableCell>
                        <TableCell align="right">{formatNumber(c.impressions)}</TableCell>
                        <TableCell align="right">{formatNumber(c.clicks)}</TableCell>
                        <TableCell align="right">{formatPercent(c.ctr)}</TableCell>
                        <TableCell align="right">{formatNumber(c.conversions)}</TableCell>
                        <TableCell align="right">{formatInr(c.spend)}</TableCell>
                      </TableRow>
                    ))}
                    {!data.campaigns.length ? (
                      <TableRow>
                        <TableCell colSpan={8}>
                          <Typography variant="body2" color="text.secondary">
                            No campaign activity for these filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      ) : null}
    </>
  );
};

export default MarketingDashboard;
